/**
 *  Usuarios y perfiles del portal (users/*) y árbol de menús (menu/get) →
 *  /api/v1/users, /api/v1/profiles y /api/v1/menus.
 *
 *  Formas viejas:
 *   - usuario: {IdPersona, Nombre, Apellidos, FechaAlta, Email, Telefono,
 *     IdPerfil, NombrePerfil, IdUnidad, IdProyecto, Propietario}
 *   - perfil: {IdPerfil, NombrePerfil, Permisos: '{"lectura":[…],"escritura":[…]}'}
 *     (Permisos viaja como texto; la página hace JSON.parse)
 *   - menú: {IdMenu, Menu, Ruta, Icono, Parent, Submenu:[…]}
 */
import api, { schemaMessage } from '../apiV1';
import { list } from './index';

const errorOf = (res) => ({
   error: true,
   message: (res.schema && schemaMessage(res.schema)) || res.message || 'Ocurrió un error, intenta de nuevo.',
});
const ok = (res) => (res.ok ? { response: true } : errorOf(res));

const toUser = (u) => ({
   IdPersona: u.id,
   Nombre: u.firstName,
   Apellidos: u.lastName,
   FechaAlta: u.createdAt,
   Email: u.email,
   Telefono: u.phone,
   IdPerfil: u.profile.id,
   NombrePerfil: u.profile.name,
   IdUnidad: u.unitId,
   IdProyecto: u.projectId,
   Propietario: u.owner,
});

// La fecha de alta ya no se captura: v1 pone la del alta y la conserva al
// editar.
const userBody = (d) => ({
   firstName: (d.Nombre || '').trim(),
   lastName: (d.Apellidos || '').trim(),
   email: (d.Email || '').trim(),
   phone: d.Telefono || '',
   profileId: Number(d.IdPerfil) || 0,
   unitId: Number(d.IdUnidad) > 0 ? Number(d.IdUnidad) : null,
});

const toProfile = (p) => ({
   IdPerfil: p.id,
   NombrePerfil: p.name,
   Permisos: JSON.stringify({ lectura: p.read || [], escritura: p.write || [] }),
});

function profileBody(d) {
   let perms = d.Permisos;
   if (typeof perms === 'string') {
      try {
         perms = JSON.parse(perms);
      } catch (e) {
         perms = {};
      }
   }
   perms = perms || {};
   return {
      name: (d.NombrePerfil || '').trim(),
      read: (perms.lectura || []).map(Number),
      write: (perms.escritura || []).map(Number),
   };
}

const toMenu = (m, parent) => ({
   IdMenu: m.id,
   Menu: m.name,
   Ruta: m.path,
   Icono: m.icon,
   Parent: parent,
   Submenu: (m.children || []).map((c) => toMenu(c, m.id)),
});

export default {
   'GET users/get': async () => {
      const res = await api.get('users');
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toUser));
   },
   'POST users/create': async (d) => {
      const res = await api.post('users', userBody(d));
      if (res.ok) return { created: true, message: '' };
      if (res.status === 409) return { created: false, message: 'Datos registrado previamente' };
      return { created: false, ...errorOf(res) };
   },
   // Status 0 es la baja.
   'POST users/update': async (d) => {
      // eslint-disable-next-line eqeqeq
      if (d.Status == 0) return ok(await api.del(`users/${d.IdPersona}`));
      return ok(await api.put(`users/${d.IdPersona}`, userBody(d)));
   },

   'GET users/get/perfiles': async () => {
      const res = await api.get('profiles');
      if (!res.ok) return errorOf(res);
      return list((res.data || []).map(toProfile));
   },
   'POST users/create/perfil': async (d) => ok(await api.post('profiles', profileBody(d))),
   // Status 0 es la baja; v1 responde 409 si el perfil está asignado.
   'POST users/update/perfil': async (d) => {
      // eslint-disable-next-line eqeqeq
      if (d.Status == 0) return ok(await api.del(`profiles/${d.IdPerfil}`));
      return ok(await api.put(`profiles/${d.IdPerfil}`, profileBody(d)));
   },

   // Árbol completo de menús para el editor de permisos de perfiles.
   'GET menu/get': async () => {
      const res = await api.get('menus');
      if (!res.ok) return errorOf(res);
      return { menu: (res.data || []).map((m) => toMenu(m, 0)) };
   },
};
